/**
 * Writes `data-theme` on `<html>` from `prefers-color-scheme`, and keeps it in sync when the OS
 * setting changes without a reload.
 *
 * **Why this file exists at all.** `app/theme.ts` hands PrimeVue `darkModeSelector:
 * '[data-theme="dark"]'`, and until now nothing ever wrote that attribute — so PrimeVue stayed in
 * its light `colorScheme` on a dark page. It mostly got away with it because the preset's light and
 * dark `semantic.colorScheme` blocks are byte-identical `var(--…)` references, which already flip
 * correctly via `tokens.css`'s own `@media (prefers-color-scheme: dark)` block. What that media
 * query does NOT reach is every Aura token the preset does not override at all — message severities,
 * overlay, mask, highlight — which stay at their light defaults regardless of the OS setting. And
 * `tokens.css` ALSO carries a `:root[data-theme="light"]` block, dead code with nothing ever writing
 * the attribute: this is what revives it, and makes an explicit choice (were one ever added, e.g. a
 * user-facing toggle) override the OS default rather than fight it.
 *
 * **Kept in sync, not just set once at startup.** A `MediaQueryList` "change" listener, not a
 * one-shot read at import time — an operator can leave the OS light/dark switch alone all shift, but
 * whichever way it changes, the next paint should not still be reading yesterday's answer.
 *
 * `window.matchMedia` is guarded rather than called unconditionally: jsdom does not implement it,
 * and nor, in principle, would a very old or embedded kiosk-adjacent browser rendering the
 * backoffice itself. A page that cannot detect the OS preference should still get a real, valid
 * `data-theme` — `light` — rather than throw before `app.mount()` ever runs.
 *
 * Under test the guard is no longer what saves this file: `src/test-setup.ts` installs a
 * `matchMedia` shim before every suite (PrimeVue's `DatePicker` calls it on mount and would
 * otherwise take out three unrelated suites). The guard remains because the shim is a TEST
 * fixture — it says nothing about the browsers this ships to, and deleting production defence
 * because a test harness happens to paper over it is how the defence gets lost.
 */

function prefersDark(media: MediaQueryList | null): boolean {
  return media?.matches ?? false;
}

function applyTheme(isDark: boolean): void {
  document.documentElement.dataset.theme = isDark ? "dark" : "light";
}

/** Call once, at startup, from `main.ts` — mirrors `app/session-guard.ts`'s
 *  `watchForExpiredSession()` and `app/storage-sync.ts`'s `watchTokenAcrossTabs()`. */
export function initThemeMode(): void {
  const media =
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-color-scheme: dark)")
      : null;

  applyTheme(prefersDark(media));

  media?.addEventListener("change", (event) => applyTheme(event.matches));
}
